<script setup lang="ts">
import { computed, ref } from 'vue';

import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import SelectionQuantity from '@/components/customizer/mix/SelectionQuantity.vue';
import { VolumetricSelection } from '@/lib/customizers/common';
import { isDefined } from '@/utils/isDefined';

const props = defineProps<{ selections: (VolumetricSelection | undefined)[] }>();

const emit = defineEmits<{
  'add-ingredient': [string];
  'remove-ingredient': [productKey: string, removeAll?: boolean];
}>();

const isMixEmpty = computed(() => props.selections.every((ing) => !ing));

const totalQuantity = computed(() =>
  props.selections.reduce((total, selection) => total + (selection?.parts ?? 0), 0),
);

const confirmedSelections = computed(() => props.selections.filter(isDefined));

const selectionsHeights = computed(() => {
  const minHeight = 40;
  const containerHeight = 416;

  const heightPerProduct: { [key: string]: { height: number } } = {};

  const partsHeight: { [parts: number]: number } = {};

  confirmedSelections.value.forEach((selection) => {
    if (partsHeight[selection.parts]) {
      heightPerProduct[selection.productKey] = {
        height: partsHeight[selection.parts],
      };
      return;
    }

    let height = containerHeight * (selection.parts / totalQuantity.value);
    height = Math.max(height, minHeight);

    heightPerProduct[selection.productKey] = { height };
    partsHeight[selection.parts] = height;
  });

  return heightPerProduct;
});

const handleAddIngredient = (productKey: string) => {
  emit('add-ingredient', productKey);
};

const handleRemoveIngredient = (productKey: string, removeAll?: boolean) => {
  emit('remove-ingredient', productKey, removeAll);
};

const activeProductKey = ref<string>();
const handleShowName = (productKey: string) => {
  activeProductKey.value = productKey;
  setTimeout(() => {
    activeProductKey.value = undefined;
  }, 1000);
};

const { 'img/close.svg': closeIcon } = nutshell;
</script>

<template>
  <div
    class="w-full border rounded-lg border-neutral-300 h-[418px]"
    data-test="selections-list"
    :class="isMixEmpty ? 'border-dashed' : 'border-solid'"
  >
    <div
      v-if="isMixEmpty"
      class="flex flex-col items-center justify-center w-full h-full text-center"
    >
      <img
        src="@/assets/mix-bag.png"
        class="w-20 h-40"
        alt="empty mix"
        data-test="empty-mix-image"
      />
      <SmallBodyText class="mt-4 text-neutral-500 w-52" data-test="empty-mix-text">
        Select ingredients to make your own yummy mix!
      </SmallBodyText>
    </div>
    <div
      v-else
      class="h-full overflow-auto divide-y divide-solid divide-neutral-300"
      data-test="selections-container"
    >
      <div
        v-for="selection in confirmedSelections"
        :key="selection.productKey"
        class="relative flex items-center justify-center transition-[height] duration-250"
        :style="{ height: `${selectionsHeights[selection.productKey].height}px` }"
      >
        <SmallBodyText
          v-if="activeProductKey === selection.productKey"
          class="absolute inset-0 flex items-center justify-center bg-white/80"
        >
          {{ selection.name }}
        </SmallBodyText>
        <UnstyledButton
          class="absolute top-2 left-2"
          data-test="remove-item"
          @click="handleRemoveIngredient(selection.productKey, true)"
          @keydown.enter="handleRemoveIngredient(selection.productKey, true)"
        >
          <img :src="closeIcon" class="shadow-lg bg-white rounded-full w-6 p-1.5" alt="remove" />
        </UnstyledButton>
        <img
          :src="selection.imageUrl"
          class="object-contain h-full cursor-pointer"
          :alt="selection.name"
          @click="handleShowName(selection.productKey)"
        />
        <SelectionQuantity
          v-model="selection.parts"
          :productKey="selection.productKey"
          class="absolute bottom-1 right-2"
          @add-ingredient="handleAddIngredient"
          @remove-ingredient="handleRemoveIngredient"
        />
      </div>
    </div>
  </div>
</template>
